import type { ToolResult } from './design-tokens';
import { getStageConfig, isStepInProgress } from './design-tokens';

/**
 * Helpers for rendering scannable reasoning-step headers without duplicating
 * the body.
 *
 * The backend sends no per-step summary, so we split the step's content into:
 *   - a TITLE  = the first sentence (models lead with their intent, so this is
 *     a naturally good one-line gist — no fragile topic/keyword guessing); and
 *   - a BODY   = everything AFTER the first sentence.
 *
 * Because the body is the remainder, the title and body never overlap. A
 * single-sentence step has an empty body and shows only its title — so short
 * steps read as one clean line instead of the same sentence twice.
 */

/** Truncate at a word boundary with an ellipsis so text never ends mid-word. */
export function truncateAtWord(text: string, max: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  const slice = trimmed.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  // Fall back to the hard slice if there's no space (one very long token).
  const base = lastSpace > max * 0.5 ? slice.slice(0, lastSpace) : slice;
  return `${base.replace(/[\s.,;:—-]+$/, '')}…`;
}

/** Strip the common inline markdown markers that would look noisy in a title. */
function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ') // fenced code
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // images
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links -> text
    .replace(/^[\s>#*+-]+/gm, '') // leading list/quote/heading markers
    .replace(/(\*\*|__|\*|_|~~)/g, '') // emphasis markers
    .replace(/\s+/g, ' ') // collapse whitespace/newlines
    .trim();
}

/** Matches an `<entity>` tag (self-closing or paired), self-closing tried
 *  first so it never partially matches the attributed opening tag. Paired
 *  bodies are bounded to 20000 chars to prevent worse-than-linear
 *  backtracking on malformed/unclosed input (mirrors entitySanitize.ts's
 *  BARE_ENTITY_UUID_RULE). A STRING so callers construct fresh `RegExp`
 *  instances per use — sharing one stateful global/sticky regex across calls
 *  causes `lastIndex` bugs. */
const ENTITY_TAG_PATTERN =
  '<entity\\b[^>]*\\/>|<entity\\b[^>]*>[\\s\\S]{0,20000}?<\\/entity>';

/** The inner display name of a paired `<entity>` tag, or '' for a
 *  self-closing tag (no display name to show). */
function entityTagVisibleText(tag: string): string {
  const m = tag.match(/<entity\b[^>]*>([\s\S]{0,20000}?)<\/entity>/i);
  return m ? m[1] : '';
}

const TITLE_MAX = 120;

/** Match the first sentence terminator (".", "!", "?"), allowing trailing
 *  closing markdown/quote markers (e.g. `Done.**`, `"Stop."`), followed by
 *  whitespace or end-of-string — i.e. the boundary between the first sentence
 *  and the rest. The optional closers keep the boundary stable when emphasis or
 *  quotes wrap the sentence, so detection doesn't depend on whether markdown was
 *  stripped first. Avoids matching abbreviations' trailing periods crudely by
 *  requiring the terminator be followed by whitespace/end (not a letter). */
const SENTENCE_END = /[.!?]["'’”)*_`\]]*(?=\s|$)/;

/** The [start, end) character ranges of every `<entity>` tag in `raw`. */
function entityTagRanges(raw: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  const re = new RegExp(ENTITY_TAG_PATTERN, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    ranges.push([m.index, m.index + m[0].length]);
    if (m.index === re.lastIndex) {
      re.lastIndex++;
    }
  }
  return ranges;
}

/** The end index (exclusive) of the first sentence in `raw`, or -1 if the
 *  content has no sentence terminator. Both the title and the body derive their
 *  split from THIS single boundary on the raw content, so they can never
 *  disagree about where the first sentence ends — previously the title split on
 *  markdown-stripped text while the body split on raw text, which let their
 *  boundaries diverge and duplicate or drop prose.
 *
 *  Entity-tag-aware: a terminator inside an `<entity>` tag (e.g. in its
 *  display name) is skipped, so the boundary always lands after a tag's
 *  closing `</entity>` — never mid-tag, which would leak a raw tag fragment
 *  into the title or leave the body starting with one. */
function firstSentenceEnd(raw: string): number {
  const ranges = entityTagRanges(raw);
  const re = new RegExp(SENTENCE_END.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const matchIndex = m.index;
    const withinTag = ranges.some(
      ([start, end]) => matchIndex >= start && matchIndex < end
    );
    if (!withinTag) {
      return m.index + m[0].length;
    }
    if (m.index === re.lastIndex) {
      re.lastIndex++;
    }
  }
  return -1;
}

/** Private-use delimiters for masking entity tags during markdown stripping.
 *  Not whitespace and not a markdown marker, so `stripMarkdown` leaves a
 *  placeholder intact (preserving inter-token spacing) and the restore step
 *  can anchor strictly on these delimiters — never on a bare digit run, which
 *  would misread literal digits in prose (e.g. `v2`, `2024`) as an index. */
const ENTITY_PLACEHOLDER_START = '';
const ENTITY_PLACEHOLDER_END = '';

/** Strip non-entity markdown from `head` while preserving `<entity>` tags
 *  verbatim, so the caller can still render them as chips. Masks each entity
 *  tag with a delimited placeholder before running the existing
 *  `stripMarkdown`, then restores the original tags afterward. */
function stripMarkdownPreservingEntities(head: string): string {
  const tags: string[] = [];
  const masked = head.replace(new RegExp(ENTITY_TAG_PATTERN, 'gi'), match => {
    const placeholder = `${ENTITY_PLACEHOLDER_START}${tags.length}${ENTITY_PLACEHOLDER_END}`;
    tags.push(match);
    return placeholder;
  });
  const stripped = stripMarkdown(masked);
  return stripped.replace(
    new RegExp(
      `${ENTITY_PLACEHOLDER_START}(\\d+)${ENTITY_PLACEHOLDER_END}`,
      'g'
    ),
    (_m, i: string) => tags[Number(i)] ?? ''
  );
}

/** Truncate `title` to `max` VISIBLE characters, never splitting an
 *  `<entity>` tag: entity tags count only their display-name length toward
 *  the cap, and a tag that would overflow is dropped whole rather than cut
 *  mid-tag. Falls back to the plain `truncateAtWord` behavior when the title
 *  has no entity tags at all. */
function truncateTitle(title: string, max: number): string {
  if (!new RegExp(ENTITY_TAG_PATTERN, 'i').test(title)) {
    return truncateAtWord(title, max);
  }

  const tokens: Array<{ isTag: boolean; value: string }> = [];
  const re = new RegExp(ENTITY_TAG_PATTERN, 'gi');
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(title)) !== null) {
    if (m.index > lastIndex) {
      tokens.push({ isTag: false, value: title.slice(lastIndex, m.index) });
    }
    tokens.push({ isTag: true, value: m[0] });
    lastIndex = m.index + m[0].length;
    if (m.index === re.lastIndex) {
      re.lastIndex++;
    }
  }
  if (lastIndex < title.length) {
    tokens.push({ isTag: false, value: title.slice(lastIndex) });
  }

  const out: string[] = [];
  let visible = 0;
  for (const token of tokens) {
    if (token.isTag) {
      const w = entityTagVisibleText(token.value).length;
      if (visible + w > max && out.length > 0) {
        const joined = out.join('');
        return `${joined.replace(/[\s.,;:—-]+$/, '')}…`;
      }
      out.push(token.value);
      visible += w;
    } else if (visible + token.value.length <= max) {
      out.push(token.value);
      visible += token.value.length;
    } else {
      const remaining = max - visible;
      const slice = token.value.slice(0, remaining);
      const lastSpace = slice.lastIndexOf(' ');
      const base =
        lastSpace > remaining * 0.5 ? slice.slice(0, lastSpace) : slice;
      const joined = out.join('');
      return `${(joined + base).replace(/[\s.,;:—-]+$/, '')}…`;
    }
  }
  return out.join('').trim();
}

/**
 * Derive the step's TITLE: the first sentence of its content, markdown-stripped
 * and length-capped (it may wrap a line or two; we cap so a runaway first
 * "sentence" can't dominate). Entity tags are preserved (not stripped) so the
 * caller can render them as chips. Returns '' when there's no usable content.
 *
 * Streaming/partial-tag safety: an unclosed opening like `<entity id="a">Acme
 * streaming` (content still arriving) does not match `ENTITY_TAG_PATTERN` —
 * both its self-closing and paired alternatives require a terminator (`/>` or
 * `</entity>`) — so it falls through untouched as literal text rather than
 * being stripped or throwing. This is safe: the same partial string also
 * reaches `MarkdownContent`'s `preprocessEntityTags` pipeline, which
 * progressively converts the opening into a chip as the rest of the tag
 * streams in, so the raw fragment is never the final rendered state.
 */
export function deriveStepTitle(content: string, max = TITLE_MAX): string {
  const raw = (content || '').trim();
  if (!raw) {
    return '';
  }
  const end = firstSentenceEnd(raw);
  const head = end === -1 ? raw : raw.slice(0, end);
  const cleaned = stripMarkdownPreservingEntities(head);
  if (!cleaned) {
    return '';
  }
  // Drop a trailing period for a cleaner title; keep ? and ! (they carry
  // tone). An entity tag always ends in '>', so this never touches a tag.
  const title = cleaned.replace(/\.$/, '');
  return truncateTitle(title, max);
}

/**
 * The step BODY: the original content with its first sentence removed, so it
 * never repeats the title. Operates on the raw content (preserving markdown for
 * rendering) and returns '' when the first sentence is the whole, untruncated
 * content.
 *
 * `title` must be the raw `deriveStepTitle(content)` result — NOT a
 * display-ready fallback value (e.g. a status label substituted in when
 * derivation is empty) — or the truncation check below would be evaluated
 * against the wrong string. Takes `title` as a parameter (rather than
 * recomputing it internally) so callers that already derived it once don't
 * pay for the regex-heavy derivation twice per render.
 *
 * Accessibility guard: when the title had to be truncated (it appends "…"), it
 * cannot show the full first sentence, so the whole content is returned as the
 * body — otherwise the tail would be unreachable in compact mode (which hides
 * the body until expanded). This covers both a runaway first sentence and
 * content with no sentence terminator at all.
 */
export function stepBodyAfterTitle(content: string, title: string): string {
  const raw = (content || '').trim();
  if (!raw) {
    return '';
  }
  if (title.endsWith('…')) {
    return raw;
  }
  const end = firstSentenceEnd(raw);
  if (end === -1) {
    return ''; // single short sentence → the title already shows all of it
  }
  return raw.slice(end).trim();
}

/**
 * Fallback title for a step where the model emitted tool calls with no
 * narration text (common with weaker models — without this every such step
 * renders as a bare "Thinking"). It SUMMARISES the tool activity
 * ("Thinking · 2 tools") rather than listing tool names. '' without tools.
 */
export function deriveToolTitle(
  stageLabel: string,
  toolResults: ToolResult[] | undefined
): string {
  if (!toolResults || toolResults.length === 0) {
    return '';
  }
  const count = toolResults.length;
  return `${stageLabel} · ${count} ${count === 1 ? 'tool' : 'tools'}`;
}

/** The fields of a reasoning step its one-line title is derived from. */
export interface StepTitleSource {
  content: string;
  stage: string;
  status: string;
  tool_results?: ToolResult[];
}

/**
 * The one-line title a reasoning step shows in the chat, in precedence order:
 * the first sentence of its narration; a tool-count summary for steps with
 * only tool calls; the stage's live status text while in progress (or
 * "Waiting for input"); finally the stage name.
 */
export function resolveStepTitle(step: StepTitleSource): string {
  const narrationTitle = deriveStepTitle(step.content);
  if (narrationTitle) {
    return narrationTitle;
  }
  const { displayName, statusText } = getStageConfig(step.stage);
  const toolTitle = deriveToolTitle(displayName, step.tool_results);
  if (toolTitle) {
    return toolTitle;
  }
  if (isStepInProgress(step.status)) {
    return statusText;
  }
  if (step.status === 'waiting_human') {
    return 'Waiting for input';
  }
  return displayName;
}
